import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';

import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n, useLanguages } from '@modules/i18n/composables';
import { utcDateToLocal } from '@utils/date';

import type { IAffiliateData, IAffiliateProfileFormSchema, IAffiliateProfileResponse } from '../types';

export interface AffiliateProfileFormComposable {
  mainSchema: ComputedRef<IAffiliateProfileFormSchema>;
  contactsSchema: ComputedRef<IAffiliateProfileFormSchema>;
  offerSchema: ComputedRef<IAffiliateProfileFormSchema>;
  subaffiliateSchema: ComputedRef<IAffiliateProfileFormSchema>;
}

export default function useAffiliateProfileForms(
  data: Ref<IAffiliateProfileResponse | undefined>,
  translationKey: Ref<symbol>,
): AffiliateProfileFormComposable {
  const { getTranslation } = useI18n();
  const { interfaceLanguages } = useLanguages();

  const profile = computed<IAffiliateData | undefined>(() => data.value?.affiliate);

  const mainSchema = computed<IAffiliateProfileFormSchema>(() => ({
    name: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'MAIN_NAME').value,
      value: profile.value?.name,
    },
    surname: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'MAIN_SURNAME').value,
      value: profile.value?.surname,
    },
    username: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'MAIN_USERNAME').value,
      value: profile.value?.username,
    },
    birthday: {
      type: EFormInputTypes.DATE,
      label: getTranslation(translationKey.value, 'MAIN_BIRTHDAY').value,
      value: profile.value?.birthday ? utcDateToLocal(profile.value?.birthday) : undefined,
    },
    site_url: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'MAIN_SITE_URL').value,
      value: profile.value?.site_url,
    },
  }));

  const contactsSchema = computed<IAffiliateProfileFormSchema>(() => ({
    email: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'CONTACT_EMAIL').value,
      value: profile.value?.email,
    },
    telegram: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'CONTACT_TELEGRAM').value,
      value: profile.value?.telegram,
    },
    ms_teams: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'CONTACT_MS_TEAMS').value,
      value: profile.value?.ms_teams,
    },
    whatsapp: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationKey.value, 'CONTACT_WHATSAPP').value,
      value: profile.value?.whatsapp,
    },
    phone_number: {
      type: EFormInputTypes.NUMBER,
      label: getTranslation(translationKey.value, 'CONTACT_PHONE').value,
      value: profile.value?.phone_number ? Number(profile.value?.phone_number) : undefined,
      disableButtons: true,
      useGrouping: false,
      maxFractionDigits: 0,
    },
    language_of_communication: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(translationKey.value, 'LANGUAGE_OF_COMMUNICATION').value,
      options: interfaceLanguages.value,
      value: profile.value?.language_of_communication,
      readonly: true,
      description: getTranslation(translationKey.value, 'LANGUAGE_OF_COMMUNICATION_DESCRIPTION').value,
      descriptionSeverity: 'info',
    },
    agree_receive_emails: {
      type: EFormInputTypes.CHECKBOX,
      label: getTranslation(translationKey.value, 'CONTACT_AGREE_RECEIVE_EMAILS').value,
      value: profile.value?.agree_receive_emails,
    },
  }));

  const offerSchema = computed<IAffiliateProfileFormSchema>(() => ({
    about_affiliate: {
      type: EFormInputTypes.TEXTAREA,
      label: getTranslation(translationKey.value, 'OFFER_ABOUT').value,
      value: profile.value?.about_affiliate,
    },
  }));

  const subaffiliateSchema = computed<IAffiliateProfileFormSchema>(() => ({
    affiliate_invitation_id: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(translationKey.value, 'AFFILIATE_INVITATION_ID').value,
      value: profile.value?.affiliate_invitation_id,
      options: data.value?.affiliates ?? [],
      optionValue: 'id',
      optionLabel: 'name',
    },
    date: {
      type: EFormInputTypes.DATE,
      label: getTranslation(translationKey.value, 'DATE').value,
      value: profile.value?.date ? utcDateToLocal(profile.value?.date) : undefined,
      required: true,
      view: 'month',
      dateFormat: 'mm.yy',
    },
  }));

  return {
    mainSchema,
    contactsSchema,
    offerSchema,
    subaffiliateSchema,
  };
}
